import React from 'react';

import IconApprove from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NextIcon from '@mui/icons-material/SkipNext';
import PrevIcon from '@mui/icons-material/SkipPrevious';
import TranslateIcon from '@mui/icons-material/Translate';
import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  apiCall,
  areKeyStatusesApproved,
  areKeyStatusesSame,
  areKeyTranslationsSame,
  emojiNumbers,
  filterWeirdSymbols,
  hash53,
  hideLoader,
  mainLangFirst,
  showLoader,
} from '../../utils';
import {Screenshot} from '../projects/Screenshot';

import {KeyStatusPerLang} from './KeyStatus';

import {
  KeyTranslation,
  Project,
  ProjectKeysStatuses,
  ProjectKeyStatuses,
  ProjectRevision,
  ScreenshotKeyConnection,
  SignRole,
  TranslationRequestDTO,
  TranslationResponseDTO,
  TranslationSource,
} from '@bumpy/i18n_common';

type Props = {
  activeKey: string;
  checkLang: string;
  keyStatuses: ProjectKeysStatuses;
  screenshotKeysConnections: ScreenshotKeyConnection[];
  project: Project;
  revision: ProjectRevision;
  role: SignRole;
  onChangeActiveKey: (newActiveKey: string) => void;
  onClose: () => void;
  onDeleteKey: () => void;
  onSave: (
    keyName: string,
    translation: KeyTranslation,
    keyStatuses: ProjectKeyStatuses,
  ) => boolean;
};

type ExpandedLangs = {[lang: string]: boolean};

export const ActiveKeyDialog: React.FC<Props> = (props) => {
  const [open, setOpen] = React.useState(true);
  const [key, setKey] = React.useState(props.activeKey);
  const langsRef = React.useRef<string[]>([]);
  const originalTranslation = React.useRef<KeyTranslation>();
  const originalKeyStatuses = React.useRef<ProjectKeyStatuses>();
  const [translations, setTranslations] = React.useState<KeyTranslation>({});
  const [keyStatuses, setKeyStatuses] = React.useState<ProjectKeyStatuses>({});
  const [expandedLangs, setExpandedLangs] = React.useState<ExpandedLangs>({});
  const [multilineLang, setMultilineLang] = React.useState('');
  const [translationDto, setTranslationDto] = React.useState<
    TranslationResponseDTO | undefined
  >();

  React.useEffect(() => {
    setKey(props.activeKey);
    const langs = mainLangFirst(
      Object.keys(props.revision.translations) as string[],
      props.project.mainLang,
    );
    const newTranslations: KeyTranslation = {};
    for (const lang of langs) {
      newTranslations[lang] =
        props.revision.translations[lang][props.activeKey];
    }
    langsRef.current = langs;
    originalTranslation.current = newTranslations;
    setTranslations(newTranslations);

    const newKeyStatuses: ProjectKeyStatuses = {};
    for (const lang of Object.keys(props.revision.translations) as string[]) {
      const statuses = props.keyStatuses[lang];
      if (statuses) {
        newKeyStatuses[lang] = statuses[props.activeKey];
      }
    }
    originalKeyStatuses.current = newKeyStatuses;
    setKeyStatuses(newKeyStatuses);
  }, [props.activeKey]);

  const [focusedLang, setFocusedLang] = React.useState<string | null>(
    props.project.mainLang,
  );
  const [activeScreenshotIndex, setActiveScreenshotIndex] = React.useState(0);
  const screenshots: {
    [screenshot: string]: {
      connections: ScreenshotKeyConnection[];
      texts?: string[];
    };
  } = {};

  const screenshotNames = new Set<string>();
  for (const connection of props.screenshotKeysConnections) {
    if (connection.key === props.activeKey) {
      if (!screenshotNames.has(connection.screenshot)) {
        screenshots[connection.screenshot] = {
          connections: [],
        };
        screenshotNames.add(connection.screenshot);
      }
    }
  }
  for (const connection of props.screenshotKeysConnections) {
    if (!screenshotNames.has(connection.screenshot)) {
      continue;
    }
    screenshots[connection.screenshot].connections.push(connection);
  }

  if (focusedLang) {
    for (const screenshotName of screenshotNames) {
      const record = screenshots[screenshotName];
      record.texts = [];
      for (const connection of record.connections) {
        if (connection.key === props.activeKey) {
          record.texts.push(translations[focusedLang]);
        } else {
          record.texts.push(
            props.revision.translations[focusedLang][connection.key],
          );
        }
      }
    }
  }
  let activeScreenshotName: string | undefined;
  if (screenshotNames.size > 0) {
    activeScreenshotName = [...screenshotNames][activeScreenshotIndex];
  }

  function translate(data: TranslationRequestDTO): void {
    showLoader('Translating...');
    apiCall({
      method: 'POST',
      url: '/translations',
      secure: true,
      data,
    })
      .then((response) => {
        const dto = response.data as TranslationResponseDTO;
        const newExpandedLangs: ExpandedLangs = {};
        const newTranslations: KeyTranslation = {
          ...translations,
        };
        for (const lang of Object.keys(dto)) {
          const translation = dto[lang][key];
          const sources = Object.keys(translation) as TranslationSource[];
          let matches = 1;
          const first = translation[sources[0]].tr[0];
          for (let j = 1; j < sources.length; ++j) {
            if (first === translation[sources[j]].tr[0]) {
              matches++;
            }
          }
          if (matches === sources.length) {
            newExpandedLangs[lang] = false;
            newTranslations[lang] = first;
          } else {
            newExpandedLangs[lang] = true;
          }
        }

        setTranslations(newTranslations);
        setTranslationDto(dto);
        setExpandedLangs(newExpandedLangs);
      })
      .finally(hideLoader);
  }

  const onTranslateSingleClick = (toLang: string) => {
    const fromLang = props.project.mainLang;
    const content = translations[fromLang];
    if (!content || content.trim().length < 2) {
      alert(`Please enter ${fromLang} valid text`);
      return;
    }
    translate({
      projectUuid: props.project.uuid,
      checkLang: props.checkLang,
      fromLang,
      content: {
        [key]: content,
      },
      toLangs: [toLang],
    });
  };

  const onTranslateClick = () => {
    const fromLang = props.project.mainLang;
    const content = translations[fromLang];
    if (!content || content.trim().length < 2) {
      alert(`Please enter ${fromLang} valid text`);
      return;
    }
    const toLangs = [...langsRef.current];
    const index = toLangs.indexOf(fromLang);
    if (index !== -1) {
      toLangs.splice(index, 1);
    }
    translate({
      projectUuid: props.project.uuid,
      checkLang: props.checkLang,
      fromLang,
      content: {
        [key]: content,
      },
      toLangs,
    });
  };

  const onSaveClick = () => {
    if (props.onSave(key, translations, keyStatuses)) {
      setOpen(false);
    }
  };

  const onCancelClick = () => {
    setOpen(false);
    props.onClose();
  };

  const renderRow = (lang: string) => {
    const isString = typeof translations[lang] === 'string';
    const expanded = expandedLangs[lang];
    const fields: React.ReactNode[] = [
      <TableRow key={`main_${lang}`}>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={1}>
            <span
              onClick={() => {
                setMultilineLang(lang);
              }}
            >
              {lang}
            </span>
            <KeyStatusPerLang
              keyName={props.activeKey}
              lang={lang}
              project={props.project}
              value={translations[lang]}
              mainLangValue={translations[props.project.mainLang]}
              keysStatusesPerLang={keyStatuses}
              onChange={(status) => {
                setKeyStatuses({
                  ...keyStatuses,
                  [lang]: {
                    status,
                    text: translations[lang],
                    main_text_hash: hash53(
                      translations[props.project.mainLang],
                    ),
                  },
                });
              }}
            />
            {translationDto && translationDto[lang] && (
              <IconButton
                size="small"
                onClick={() => {
                  setExpandedLangs({
                    ...expandedLangs,
                    [lang]: !expanded,
                  });
                }}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row-reverse">
            {lang !== props.project.mainLang && props.role === 'admin' && (
              <IconButton
                size="small"
                onClick={() => {
                  onTranslateSingleClick(lang);
                }}
              >
                <TranslateIcon />
              </IconButton>
            )}
            <TextField
              fullWidth
              multiline={lang === multilineLang}
              margin="none"
              size="small"
              disabled={
                props.role === 'translator' && lang === props.project.mainLang
              }
              value={
                isString
                  ? translations[lang]
                  : JSON.stringify(translations[lang])
              }
              onFocus={() => {
                setFocusedLang(lang);
              }}
              onBlur={() => {
                setFocusedLang(null);
              }}
              onChange={(event) => {
                let value = filterWeirdSymbols(event.target.value);
                if (!isString) {
                  try {
                    value = JSON.parse(value);
                  } catch {}
                }
                setTranslations({
                  ...translations,
                  [lang]: value,
                });
              }}
            />
          </Stack>
        </TableCell>
      </TableRow>,
    ];

    if (translationDto) {
      const translationLang = translationDto[lang];
      if (translationLang) {
        const translationKey = translationLang[key];
        if (translationKey) {
          const sourceRows: React.ReactNode[] = [];
          const variants: string[] = [];
          for (const source of Object.keys(
            translationKey,
          ) as TranslationSource[]) {
            const tr = translationKey[source];
            const variant = tr.tr[0];
            let emojiNumber: string;
            const index = variants.indexOf(variant);
            if (index === -1) {
              variants.push(variant);
              emojiNumber = emojiNumbers[variants.length - 1];
            } else {
              emojiNumber = emojiNumbers[index];
            }
            sourceRows.push(
              <TableRow key={source}>
                <TableCell
                  onClick={() => {
                    setTranslations({
                      ...translations,
                      [lang]: variant,
                    });
                  }}
                >
                  {source}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center">
                    {emojiNumber}
                    <Stack direction="column">
                      <TextField
                        fullWidth
                        margin="none"
                        size="small"
                        contentEditable="false"
                        value={variant}
                      />
                      {tr.check && <span>{tr.check}</span>}
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>,
            );
          }
          fields.push(
            <TableRow key={`extra_${lang}`}>
              <TableCell style={{paddingLeft: 20, padding: 0}} colSpan={6}>
                <Collapse in={expanded} timeout="auto">
                  <Table size="small" aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        <TableCell width={70}>Source</TableCell>
                        <TableCell>Translation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{sourceRows}</TableBody>
                  </Table>
                </Collapse>
              </TableCell>
            </TableRow>,
          );
        }
      }
    }

    return fields;
  };

  return (
    <Dialog
      open={open}
      onClose={onCancelClick}
      sx={
        screenshotNames.size > 0
          ? {'& .MuiDialog-paper': {width: '100%', maxWidth: '1200px'}}
          : undefined
      }
    >
      <DialogTitle>
        <Stack direction="row-reverse">
          {props.role === 'admin' && (
            <IconButton onClick={onTranslateClick}>
              <TranslateIcon />
            </IconButton>
          )}
          <TextField
            fullWidth
            value={key}
            disabled={props.role !== 'admin'}
            size="small"
            label="Key"
            margin="none"
            onChange={(event) => {
              setKey(event.target.value);
            }}
          />
          {props.role === 'admin' && (
            <IconButton
              disabled={areKeyStatusesApproved(langsRef.current, keyStatuses)}
              color="success"
              onClick={() => {
                const newKeyStatuses: ProjectKeyStatuses = {};
                for (const lang of langsRef.current) {
                  newKeyStatuses[lang] = {
                    status: 'Approved',
                    text: translations[lang],
                    main_text_hash: hash53(
                      translations[props.project.mainLang],
                    ),
                  };
                }
                setKeyStatuses(newKeyStatuses);
              }}
            >
              <IconApprove />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent inputMode="text">
        <Stack direction="row">
          <TableContainer sx={{maxHeight: 750}}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={100}>Lang</TableCell>
                  <TableCell width={1200}>Translation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{langsRef.current.map(renderRow)}</TableBody>
            </Table>
          </TableContainer>
          {activeScreenshotName && (
            <Stack>
              <Stack direction="row" justifyContent="space-between">
                <IconButton
                  disabled={activeScreenshotIndex === 0}
                  onClick={() => {
                    setActiveScreenshotIndex(activeScreenshotIndex - 1);
                  }}
                >
                  <PrevIcon />
                </IconButton>

                <Typography>{activeScreenshotName}</Typography>

                <IconButton
                  disabled={activeScreenshotIndex === screenshotNames.size - 1}
                  onClick={() => {
                    setActiveScreenshotIndex(activeScreenshotIndex + 1);
                  }}
                >
                  <NextIcon />
                </IconButton>
              </Stack>
              <Screenshot
                key={activeScreenshotName}
                connections={screenshots[activeScreenshotName].connections}
                texts={screenshots[activeScreenshotName].texts}
                projectUuid={props.project.uuid}
                screenshotName={activeScreenshotName}
                onKeyPressed={props.onChangeActiveKey}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" flex={1} justifyContent="space-between">
          {props.role === 'admin' ? (
            <Button
              color="warning"
              startIcon={<DeleteIcon />}
              onClick={props.onDeleteKey}
            >
              Delete key
            </Button>
          ) : (
            <div />
          )}
          <Stack direction="row">
            <Button onClick={onCancelClick}>Cancel</Button>
            <Button
              disabled={
                key.trim().length === 0 ||
                (key === props.activeKey &&
                  areKeyTranslationsSame(
                    langsRef.current!,
                    translations,
                    originalTranslation.current!,
                  ) &&
                  areKeyStatusesSame(
                    langsRef.current!,
                    keyStatuses,
                    originalKeyStatuses.current!,
                  ))
              }
              onClick={onSaveClick}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
